import { attach, createEvent, createStore, guard, restore, sample } from 'effector'
import { useEffect } from 'react'

import { getCarsFx } from '../../../api/cars'
import { logger } from '../../../lib/logger'
import { getFromStorage, setToStorage } from '../../../lib/storage'
import { fetchCarsOnListFx } from '../../cars-list/model'
import { $tradeInEnabled, $tradeInPrice, $widgetIDByDealer } from '../../smart-contract/model'

type CreditFilterValues = {
  initialFee: number
  buybackPayment: number
  creditPayment: number
  creditTerms: number
}

export const setCreditFilterValues = createEvent<CreditFilterValues>()
export const $creditFilterValues = restore(setCreditFilterValues, null)

export const setChosenCreditFilterValues = createEvent<CreditFilterValues>()

/**
 * Это те значения кредитного фильтра, которые были выбраны и применены пользователем
 */

export const $chosenCreditFilterValues = restore(
  setChosenCreditFilterValues,
  getFromStorage('creditFilterFormState') || null
)

guard({
  source: $chosenCreditFilterValues,
  filter: Boolean
}).watch((value) => setToStorage('creditFilterFormState', value))

/**
 * Это текущие значения формы. После применения формы они будут так же находиться в setChosenCreditFilterValues
 */

export const setCreditFilterEnabled = createEvent<boolean>()

export const $creditFilterEnabled = restore(setCreditFilterEnabled, !!getFromStorage('creditFilterEnabled'))
$creditFilterEnabled.updates.watch((v) => setToStorage('creditFilterEnabled', v))

export const setWidgetId = createEvent<string>()
const $widgetId = restore(setWidgetId, null)

export const setCreditAvailable = createEvent<boolean>()
export const $creditAvailable = restore(setCreditAvailable, null)

const $baseRequestData = sample({
  source: [$tradeInEnabled, $tradeInPrice, $widgetId, $creditAvailable, $widgetIDByDealer],
  fn: ([tradeInEnabled, tradeInPrice, widgetId, available, widgetIds]) => {
    if (!available) return null
    if (tradeInPrice === null) return null
    if (tradeInEnabled === null) return null
    if (widgetId === null) return null
    if (widgetIds === null) return null

    return {
      trade_in_price: tradeInEnabled && tradeInPrice ? tradeInPrice : 0,
      custom_benefit: 0,
      widget_id: widgetId,
      widget_ids: widgetIds
    }
  }
})

export const setCreditFormOpened = createEvent<boolean>()
const $creditFormOpened = restore(setCreditFormOpened, false)

$tradeInEnabled.watch((enabled) => logger.log('setTradeInEnabled', enabled))
$tradeInPrice.watch((enabled) => logger.log('tradeInPrice', enabled))

/**
 * Параметры запроса, которые будут использованы для запроса машин на списке и для запроса на подсчёт автомобилей  для формы
 *
 */
export const $chosenCreditFilterRequestData = sample({
  source: [$chosenCreditFilterValues, $creditFilterEnabled, $baseRequestData],
  fn: ([chosenValues, enabled, baseRequestData]) => {
    if (!chosenValues) return null
    if (!enabled) return null
    if (baseRequestData === null) return null

    return {
      initial_fee: chosenValues.initialFee,
      residual_payment: chosenValues.buybackPayment,
      period: chosenValues.creditTerms,
      max_per_month: chosenValues.creditPayment,
      ...baseRequestData
    }
  }
})

export const $creditFilterCountRequestData = sample({
  source: [$creditFilterValues, $baseRequestData, $creditFormOpened],
  fn: ([creditValues, baseRequestData, opened]) => {
    if (!opened) return null
    if (!baseRequestData) return null
    if (!creditValues) return null
    return {
      initial_fee: creditValues.initialFee,
      residual_payment: creditValues.buybackPayment,
      period: creditValues.creditTerms,
      max_per_month: creditValues.creditPayment,
      ...baseRequestData
    }
  }
})

$baseRequestData.watch((d) => logger.log('base request data', d))
$chosenCreditFilterRequestData.watch((d) => logger.log('chosen', d))
$creditFilterCountRequestData.watch((d) => logger.log('state', d))

export const fetchCarsOnCreditFormFx = attach({
  effect: getCarsFx
})

export const $totalCount = createStore(0)
$totalCount.on(fetchCarsOnCreditFormFx.doneData, (_, data) => data.total_count)
export const $fetching = fetchCarsOnCreditFormFx.pending
